import React, { useEffect, useState } from 'react';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import useLicenseTypeManagement from './hooks/useLicenseTypeManagement';
import { useNavigate, useParams } from 'react-router';
import Loading from '../../Common/components/Loading';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Textarea } from '@/src/shadComponents/ui/textarea';
import { Label } from '@/src/shadComponents/ui/label';
import { Card, CardContent } from '@/src/shadComponents/ui/card';
import Table, { createColumnHelper } from '../../Common/components/Table';
import * as Yup from 'yup';
import { toast } from 'sonner';
import useFeatureFlagManagment from '@/src/Features/components/hooks/useFeatureFlagManagement';
import { Loader2 } from 'lucide-react';
import PageShell from '@/src/Common/components/PageShell';

interface LicenseType {
    id: number;
    name: string;
    key: string;
    description: string | null;
    adminName: string;
    simultaneousDeviceCount: number;
}

interface LicenseTypeFeature {
    id: number;
    licenseTypeId: number;
    featureId: number;
    key: string;
}

interface Feature {
    id: number;
    key: string;
}

const validationSchema = Yup.object({
    name: Yup.string()
        .required('Name is required')
        .min(3, 'Name must be at least 3 characters')
        .max(50, 'Name must be less than 50 characters'),
    description: Yup.string()
        .required('Description is required')
        .max(500, 'Description must be less than 500 characters'),
    adminName: Yup.string()
        .required('Admin name is required')
        .min(3, 'Admin name must be at least 3 characters')
        .max(50, 'Admin name must be less than 50 characters'),
    simultaneousDeviceCount: Yup.number()
        .required('Device limit is required')
        .min(1, 'Device limit must be at least 1')
        .max(1000, 'Device limit must be less than 1000'),
});

const featureColumnHelper = createColumnHelper<Feature>();

export default function EditLicenseType() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [licenseType, setLicenseType] = useState<LicenseType | null>(null);
    const [features, setFeatures] = useState<Feature[]>([]);
    const [selectedFeatures, setSelectedFeatures] = useState<
        LicenseTypeFeature[]
    >([]);
    const {
        getLicenseType,
        updateLicenseType,
        listFeaturesForLicenseType,
        addFeatureToLicenseType,
        removeFeatureFromLicenseType,
        isLoading,
        setIsLoading,
    } = useLicenseTypeManagement();

    const { fetchFeatureFlags } = useFeatureFlagManagment();

    const featureColumnDefinitions = [
        featureColumnHelper.accessor('key', {
            id: 'key',
            header: () => <span>Feature</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        featureColumnHelper.display({
            id: 'actions',
            cell: (props) => {
                const feature = props.row.original;
                const isSelected = selectedFeatures.some(
                    (f) => f.featureId === feature.id,
                );

                return (
                    <Button
                        variant={isSelected ? 'destructive' : 'outline'}
                        size="sm"
                        onClick={() => toggleFeature(feature)}
                    >
                        {isSelected ? 'Remove' : 'Add'}
                    </Button>
                );
            },
        }),
    ];

    const toggleFeature = async (feature: Feature) => {
        if (!licenseType) return;

        const isSelected = selectedFeatures.some(
            (f) => f.featureId === feature.id,
        );

        try {
            if (isSelected) {
                await removeFeatureFromLicenseType(licenseType.id, feature.id);
                setSelectedFeatures(
                    selectedFeatures.filter((f) => f.featureId !== feature.id),
                );
                toast.success(`Removed feature: ${feature.key}`);
            } else {
                const newFeature = await addFeatureToLicenseType(
                    licenseType.id,
                    feature.id,
                );
                setSelectedFeatures([...selectedFeatures, newFeature]);
                toast.success(`Added feature: ${feature.key}`);
            }
        } catch (error) {
            console.error('Failed to toggle feature:', error);
            if (error instanceof Error) {
                toast.error(error.message || 'Failed to toggle feature');
            }
        }
    };

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            if (!id) return;

            try {
                const [licenseType, features, featureFlags] = await Promise.all(
                    [
                        getLicenseType(parseInt(id)),
                        listFeaturesForLicenseType(parseInt(id)),
                        fetchFeatureFlags(),
                    ],
                );

                if (isMounted) {
                    setLicenseType(licenseType);
                    setSelectedFeatures(features);
                    setFeatures(featureFlags);
                }
            } catch (error) {
                console.error('Failed to load license type:', error);
                if (error instanceof Error) {
                    toast.error(error.message || 'Failed to load license type');
                }
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (isLoading) {
        return <Loading isLoading={isLoading} />;
    }

    if (!licenseType) {
        return <div>License type not found</div>;
    }

    return (
        <PageShell
            title="Edit License Type"
            breadcrumbs={[
                { label: 'Admin', href: '/admin/licenses' },
                { label: 'License Types', href: '/admin/licenses' },
                { label: 'Edit' },
            ]}
        >
            <div className="space-y-10">
                <Formik
                    initialValues={licenseType}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            await updateLicenseType(licenseType.id, values);
                            toast.success('License type updated successfully');
                            navigate(-1);
                        } catch (error) {
                            console.error(
                                'Failed to update license type:',
                                error,
                            );
                            if (error instanceof Error) {
                                toast.error(
                                    error.message ||
                                        'Failed to update license type',
                                );
                            }
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ isSubmitting, errors, touched }) => (
                        <FormikForm
                            className="mt-6"
                            placeholder={undefined}
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                        >
                            <div className="space-y-12">
                                <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                                    <div className="px-8">
                                        <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                            License Type Details
                                        </h2>
                                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                                            Update the properties of this
                                            license type.
                                        </p>
                                    </div>
                                    <Card className="md:col-span-2">
                                        <CardContent className="pt-6 space-y-6">
                                            <div className="space-y-2">
                                                <Label htmlFor="name">Name</Label>
                                                <Field
                                                    as={Input}
                                                    id="name"
                                                    name="name"
                                                />
                                                <ErrorMessage
                                                    name="name"
                                                    component="div"
                                                    className="text-sm text-destructive"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="description">Description</Label>
                                                <Field
                                                    as={Textarea}
                                                    id="description"
                                                    name="description"
                                                    rows={3}
                                                />
                                                <ErrorMessage
                                                    name="description"
                                                    component="div"
                                                    className="text-sm text-destructive"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="adminName">Admin Name</Label>
                                                <Field
                                                    as={Input}
                                                    id="adminName"
                                                    name="adminName"
                                                />
                                                <ErrorMessage
                                                    name="adminName"
                                                    component="div"
                                                    className="text-sm text-destructive"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="simultaneousDeviceCount">Device Limit</Label>
                                                <Field
                                                    as={Input}
                                                    type="number"
                                                    id="simultaneousDeviceCount"
                                                    name="simultaneousDeviceCount"
                                                    min="1"
                                                />
                                                <ErrorMessage
                                                    name="simultaneousDeviceCount"
                                                    component="div"
                                                    className="text-sm text-destructive"
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-4 pt-6">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => navigate(-1)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                                    {isSubmitting ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        </FormikForm>
                    )}
                </Formik>

                <div className="px-8 pb-16">
                    <h3 className="text-base font-semibold text-foreground pb-2">
                        Features
                    </h3>
                    <p className="text-sm text-muted-foreground pb-4">
                        Manage which features are available with this license
                        type.
                    </p>
                    <Table data={features} columns={featureColumnDefinitions} />
                </div>
            </div>
        </PageShell>
    );
}
